import React, { useState } from 'react';
import { AlertTriangle, Bell, ChevronDown, ChevronUp, Plus, X, Mail, Slack, MessageSquare, Check, Send, Loader2 } from 'lucide-react';

interface AlertRule {
  id: string;
  metric: string;
  condition: 'above' | 'below';
  threshold: number;
  timeframe: string;
  channels: {
    type: 'slack' | 'teams' | 'email';
    target: string;
  }[];
}

export function SetupRevenueAlerts() {
  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const [testingRuleId, setTestingRuleId] = useState<string | null>(null);
  const [testStatus, setTestStatus] = useState<Record<string, { success: boolean; error?: string }>>({});
  const [revenueAlertRules, setRevenueAlertRules] = useState<AlertRule[]>([
    {
      id: 'mrr_change',
      metric: 'mrr',
      condition: 'below',
      threshold: 5000,
      timeframe: '24h',
      channels: [{ type: 'slack', target: '#revenue-alerts' }]
    },
    {
      id: 'churn_spike',
      metric: 'churn_rate',
      condition: 'above',
      threshold: 2,
      timeframe: '24h',
      channels: [{ type: 'email', target: 'team@company.com' }]
    }
  ]);

  const handleRuleClick = (ruleId: string) => {
    setExpandedRuleId(expandedRuleId === ruleId ? null : ruleId);
  };

  const handleAddRule = () => {
    const newRule: AlertRule = {
      id: `rule_${Date.now()}`,
      metric: 'mrr',
      condition: 'below',
      threshold: 5000,
      timeframe: '24h',
      channels: [{ type: 'email', target: '' }]
    };
    setRevenueAlertRules([...revenueAlertRules, newRule]);
    setExpandedRuleId(newRule.id);
  };

  const handleTestAlert = async (rule: AlertRule) => {
    setTestingRuleId(rule.id);
    setTestStatus(prev => ({ ...prev, [rule.id]: { success: false } }));
    
    try {
      // Simulate API call to test alert - randomly succeed or fail
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Randomly fail some tests for demonstration
      if (Math.random() > 0.5) {
        throw new Error('Failed to deliver to some channels');
      }
    
      setTestStatus(prev => ({ ...prev, [rule.id]: { success: true } }));
    } catch (error) {
      setTestStatus(prev => ({ 
        ...prev, 
        [rule.id]: { 
          success: false, 
          error: error instanceof Error ? error.message : 'Unknown error occurred'
        } 
      }));
    } finally {
      setTestingRuleId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start space-x-3">
        <div className="p-2 bg-indigo-100 rounded-lg">
          <AlertTriangle className="w-6 h-6 text-indigo-600" />
        </div>
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900">
            Configure Revenue Alerts
          </h2>
          <p className="text-gray-600">
            Set up alerts to monitor key revenue metrics and get notified of important changes.
          </p>
        </div>
      </div>

      <div className="space-y-4 ml-11">
        {/* Information Sections */}
        <div className="grid grid-cols-2 gap-6 mb-8">
          <div className="space-y-2">
            <div className="flex items-center space-x-2 text-gray-900">
              <Bell className="w-4 h-4 text-indigo-600" />
              <span className="font-medium">Revenue Alerts</span>
            </div>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-600 mr-2" />
                MRR changes
              </li>
              <li className="flex items-center">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-600 mr-2" />
                Churn spikes
              </li>
              <li className="flex items-center">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-600 mr-2" />
                Payment failures
              </li>
            </ul>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-center space-x-2 text-gray-900">
              <Bell className="w-4 h-4 text-indigo-600" />
              <span className="font-medium">Billing Alerts</span>
            </div>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-600 mr-2" />
                Failed charges
              </li>
              <li className="flex items-center">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-600 mr-2" />
                Subscription changes
              </li>
              <li className="flex items-center">
                <div className="w-1.5 h-1.5 rounded-full bg-indigo-600 mr-2" />
                Dunning events
              </li>
            </ul>
          </div>
        </div>

        {/* Revenue Alert Rules */}
        <div className="bg-white rounded-xl p-6 border border-gray-200 space-y-6">
          <h3 className="font-medium text-gray-900 mb-4">Revenue Alert Rules</h3>
          
          <div className="space-y-4">
            {revenueAlertRules.map((rule) => (
              <div
                key={rule.id}
                className="border border-gray-200 rounded-lg overflow-hidden"
              >
                <div
                  onClick={() => handleRuleClick(rule.id)}
                  className={`
                    flex items-center justify-between p-4 cursor-pointer
                    ${expandedRuleId === rule.id ? 'bg-indigo-50' : 'bg-gray-50'}
                    transition-colors duration-200
                  `}
                >
                  <div className="flex items-center space-x-3">
                    <Bell className="w-5 h-5 text-indigo-600" />
                    <span className="font-medium text-gray-900">
                      Alert when{' '}
                      <span>{rule.metric === 'mrr' ? 'Monthly Recurring Revenue' : 'Churn Rate'}</span>
                      {' '}is{' '}
                      <span>{rule.condition}</span>
                      {' '}
                      <span className="text-lg font-bold text-indigo-600">
                        {rule.threshold}
                        <span className="text-sm ml-0.5">
                          {rule.metric === 'mrr' ? '$' : '%'}
                        </span>
                      </span>
                    </span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <div className="flex -space-x-2">
                      {rule.channels.map((channel, index) => (
                        <div 
                          key={index}
                          className="w-8 h-8 rounded-full bg-white border-2 border-gray-200 flex items-center justify-center shadow-sm"
                          title={channel.target}
                        >
                          {channel.type === 'slack' && <Slack className="w-4 h-4 text-[#4A154B]" />}
                          {channel.type === 'teams' && <MessageSquare className="w-4 h-4 text-[#6264A7]" />}
                          {channel.type === 'email' && <Mail className="w-4 h-4 text-gray-600" />}
                        </div>
                      ))}
                    </div>
                    {expandedRuleId === rule.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
                
                {expandedRuleId === rule.id && (
                  <div className="p-4 border-t border-gray-200 space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Metric
                        </label>
                        <select
                          value={rule.metric}
                          onChange={(e) => {
                            const newRules = revenueAlertRules.map(r =>
                              r.id === rule.id ? { ...r, metric: e.target.value } : r
                            );
                            setRevenueAlertRules(newRules);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="mrr">Monthly Recurring Revenue</option>
                          <option value="churn_rate">Churn Rate</option>
                          <option value="payment_failure">Payment Failure Rate</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Condition
                        </label>
                        <select
                          value={rule.condition}
                          onChange={(e) => {
                            const newRules = revenueAlertRules.map(r =>
                              r.id === rule.id ? { ...r, condition: e.target.value as 'above' | 'below' } : r
                            );
                            setRevenueAlertRules(newRules);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        >
                          <option value="above">Above</option>
                          <option value="below">Below</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Threshold
                        </label>
                        <input
                          type="number"
                          value={rule.threshold}
                          onChange={(e) => {
                            const newRules = revenueAlertRules.map(r =>
                              r.id === rule.id ? { ...r, threshold: Number(e.target.value) } : r
                            );
                            setRevenueAlertRules(newRules);
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Timeframe
                      </label>
                      <select
                        value={rule.timeframe}
                        onChange={(e) => {
                          const newRules = revenueAlertRules.map(r =>
                            r.id === rule.id ? { ...r, timeframe: e.target.value } : r
                          );
                          setRevenueAlertRules(newRules);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="1h">1 hour</option>
                        <option value="6h">6 hours</option>
                        <option value="24h">24 hours</option>
                        <option value="7d">7 days</option>
                      </select>
                    </div>

                    {/* Notification Channels */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Notification Channels
                      </label>
                      <div className="space-y-3">
                        {rule.channels.map((channel, index) => (
                          <div key={index} className="flex items-center space-x-3">
                            <select
                              value={channel.type}
                              onChange={(e) => {
                                const newRules = revenueAlertRules.map(r =>
                                  r.id === rule.id ? {
                                    ...r,
                                    channels: r.channels.map((ch, i) =>
                                      i === index ? { ...ch, type: e.target.value as 'slack' | 'teams' | 'email' } : ch
                                    )
                                  } : r
                                );
                                setRevenueAlertRules(newRules);
                              }}
                              className="w-32 px-3 py-2 border border-gray-300 rounded-lg"
                            >
                              <option value="slack">Slack</option>
                              <option value="teams">Teams</option>
                              <option value="email">Email</option>
                            </select>
                            <input
                              type="text"
                              value={channel.target}
                              onChange={(e) => {
                                const newRules = revenueAlertRules.map(r =>
                                  r.id === rule.id ? {
                                    ...r,
                                    channels: r.channels.map((ch, i) =>
                                      i === index ? { ...ch, target: e.target.value } : ch
                                    )
                                  } : r
                                );
                                setRevenueAlertRules(newRules);
                              }}
                              placeholder={
                                channel.type === 'slack'
                                  ? '#channel'
                                  : channel.type === 'teams'
                                    ? '#channel'
                                    : 'email@company.com'
                              }
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
                            />
                            <button
                              onClick={() => {
                                const newRules = revenueAlertRules.map(r =>
                                  r.id === rule.id ? {
                                    ...r,
                                    channels: r.channels.filter((_, i) => i !== index)
                                  } : r
                                );
                                setRevenueAlertRules(newRules);
                              }}
                              className="p-2 text-gray-400 hover:text-gray-600"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => {
                          const newRules = revenueAlertRules.map(r =>
                            r.id === rule.id ? {
                              ...r,
                              channels: [...r.channels, { type: 'slack', target: '' }]
                            } : r
                          );
                          setRevenueAlertRules(newRules);
                        }}
                        className="mt-2 inline-flex items-center text-sm text-indigo-600 hover:text-indigo-700"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Channel
                      </button>
                    </div>
                    
                    {/* Test Alert Section */}
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-1">Test Alert</h4>
                          <p className="text-sm text-gray-600">
                            Send a test notification to verify your alert configuration
                          </p>
                        </div>
                        <button
                          onClick={() => handleTestAlert(rule)}
                          disabled={testingRuleId !== null}
                          className={`
                            inline-flex items-center px-4 py-2 rounded-lg font-medium
                            ${testingRuleId === rule.id 
                              ? 'bg-gray-100 text-gray-400 cursor-wait' 
                              : 'bg-indigo-600 text-white hover:bg-indigo-700'
                            }
                          `}
                        >
                          {testingRuleId === rule.id ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Test Alert
                            </>
                          )}
                        </button>
                      </div>
                      
                      {/* Test Success Feedback */}
                      {testStatus[rule.id]?.success && (
                        <div className="flex items-center bg-green-50 text-green-700 p-4 rounded-lg border border-green-200 animate-fade-in">
                          <Check className="w-5 h-5 mr-3" />
                          <div>
                            <h5 className="font-medium mb-1">Test Alert Sent Successfully!</h5>
                            <p className="text-sm">
                              Check your configured notification channels to verify the alert was received.
                            </p>
                          </div>
                        </div>
                      )}
                      
                      {/* Test Error Feedback */}
                      {testStatus[rule.id]?.error && (
                        <div className="flex items-center bg-red-50 text-red-700 p-4 rounded-lg border border-red-200 animate-fade-in">
                          <AlertTriangle className="w-5 h-5 mr-3" />
                          <div>
                            <h5 className="font-medium mb-1">Test Alert Failed</h5>
                            <p className="text-sm">
                              {testStatus[rule.id].error}. Please verify your channel configuration and try again.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}

            <button
              onClick={handleAddRule}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-300 hover:text-indigo-600 transition-colors"
            >
              <div className="flex items-center justify-center">
                <Plus className="w-5 h-5 mr-2" />
                Add Alert Rule
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
